import { Link } from "@remix-run/react";
import type { ComponentPropsWithoutRef } from "react";

import rangeBgFallback from "~/assets/images/fallback/range-background-fallback.jpg";
import {
  AccessoriesIcon,
  FacadeIcon,
  FloorIcon,
  PitchedRoofIcon,
  RoofTerraceIcon,
} from "~/components/svg/supports-categories";
import { Card } from "~/components/ui/cards/card";
import { Image } from "~/components/ui/image";
import { Text } from "~/components/ui/typography";
import { cn } from "~/utils/classnames";

interface CompatibleRangeCardProps extends Omit<ComponentPropsWithoutRef<typeof Link>, "to"> {
  title: string;
  brandName: string;
  brandImage: string;
  backgroundImage: string;
  supportTypes?: string[] | undefined;
  slug?: string;
}

export const CompatibleRangeCard = ({
  title = "Toiture inclinée",
  brandName,
  brandImage,
  backgroundImage,
  supportTypes,
  slug,
  className,
  ...props
}: CompatibleRangeCardProps) => {
  const CategorySvg = ({ className, category }: { className: string; category: string }) => {
    if (category === "pitchedRoof") {
      return <PitchedRoofIcon className={className} />;
    }
    if (category === "roofTerrace") {
      return <RoofTerraceIcon className={className} />;
    }
    if (category === "facade") {
      return <FacadeIcon className={className} />;
    }
    if (category === "floor") {
      return <FloorIcon className={className} />;
    }
    if (category === "accessories") {
      return <AccessoriesIcon className={className} />;
    }
    return null;
  };

  return (
    <Link to={`/systemes-de-fixation/gamme/${slug}`} {...props}>
      <Card
        className={cn(
          "relative h-full w-full overflow-hidden text-white transition-shadow hover:shadow-md",
          className
        )}
      >
        <Image
          src={backgroundImage}
          fallbackSrc={rangeBgFallback}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover brightness-[40%]"
        />

        <div className="absolute left-0 top-4 flex min-w-16 items-center justify-center overflow-hidden rounded-r-lg border border-l-0 border-grey-100 bg-white px-1.5 py-0.5 text-xs font-medium text-grey-900">
          {brandImage ? (
            <Image
              src={brandImage}
              alt={brandName}
              className="h-9 object-contain"
              fallbackElement={<div>{brandName}</div>}
            />
          ) : (
            <div>{brandName}</div>
          )}
        </div>

        <div className="relative">
          <Text size="sm" className="mt-12">
            Gamme
          </Text>
          <Text className="font-medium">{title}</Text>
          <div className="mt-2 flex flex-wrap gap-2">
            {supportTypes?.map((support) => (
              <div className="rounded-full bg-white p-2" key={support}>
                <CategorySvg className="size-6" category={support} />
              </div>
            ))}
          </div>
        </div>
      </Card>
    </Link>
  );
};
